import { useEffect, useState } from "react";
import { colorService } from "../../../Services/color.service";
import { configsService } from "../../../Services/configs.service";
import {
  buildReportConfigFromApi,
  ensureHighSpeedDefaults,
} from "../../../components/ReportConfig/vehicleGroupReportConfig.utils";

/**
 * Normaliza o valor de formato de anexo para o código esperado pela API.
 * Corrige dados antigos/inválidos vindos do localStorage ou do servidor.
 *
 *   "pdf" | "PDF" | "p" → "P"
 *   "excel" | "EXCEL" | "e" → "E"
 *   "kmz"  | "KMZ"  | "k" → "K"
 *   já correto → mantém     |  inválido → fallback
 */
function normalizeFormat(value, fallback = "P") {
  if (!value) return fallback;
  const map = { pdf: "P", p: "P", excel: "E", e: "E", kmz: "K", k: "K" };
  return map[String(value).toLowerCase()] ?? fallback;
}

/**
 * Carrega e gerencia o estado do grupo de veículos e todos os formulários
 * de configuração da tela VehiclesConfig.
 *
 * @param {{ vehicleGroupId: number|string, initialStoredState: object }} params
 *   `initialStoredState` é lido do localStorage uma única vez no mount e passado
 *   aqui para que o hook respeite os valores persistidos sem precisar acessar
 *   o localStorage diretamente.
 */
export function useGroupReportConfig({ vehicleGroupId, initialStoredState }) {
  const [isLoading, setIsLoading] = useState(true);
  const [listCollor, setListCollor] = useState([]);
  const [group, setGroup] = useState();

  const [selectedColor, setSelectedColor] = useState(
    initialStoredState.selectedColor || ""
  );
  const [selectedId, setSelectedId] = useState(
    initialStoredState.selectedId || ""
  );
  const [kmzFlag, setKmzFlag] = useState(initialStoredState.kmzFlag || false);
  const [fenceFlag, setFenceFlag] = useState(
    initialStoredState.fenceFlag || false
  );
  const [visitFlag, setVisitFlag] = useState(
    initialStoredState.visitFlag || false
  );
  const [riskAreaFlag, setRiskAreaFlag] = useState(
    initialStoredState.riskAreaFlag || false
  );
  const [strechesFlag, setStrechesFlag] = useState(
    initialStoredState.strechesFlag || false
  );

  const [relatorios, setRelatorios] = useState(
    initialStoredState.relatorios || {
      kmz: false,
      movimentacao: false,
      pontoDeVista: false,
      areaDeRisco: false,
      cercaEletronica: false,
      visitsSummaryXlsPeriod: false,
      visitsSummaryPdfPeriod: false,
      visitsAmountPdfPeriod: false,
      visitsAmountXlsPeriod: false,
      speedSummaryPdfPeriod: false,
      speedSummaryXlsPeriod: false,
      maintenanceSummaryPdfPeriod: false,
      maintenanceSummaryXlsPeriod: false,
    }
  );

  // Normaliza formatos de anexo ao ler do localStorage (sanitiza dados antigos com "pdf" etc.)
  const [archives, setArchives] = useState(() => {
    const stored = initialStoredState.archives;
    if (stored) {
      return {
        ...stored,
        archiveKmz: normalizeFormat(stored.archiveKmz, "K"),
        archiveMovimentacao: normalizeFormat(stored.archiveMovimentacao, "P"),
        archivePontoDeVista: normalizeFormat(stored.archivePontoDeVista, "P"),
        archiveAreaDeRisco: normalizeFormat(stored.archiveAreaDeRisco, "P"),
        archiveCercaEletronica: normalizeFormat(stored.archiveCercaEletronica, "P"),
      };
    }
    return {
      archiveKmz: "K",
      archiveMovimentacao: "P",
      archivePontoDeVista: "P",
      archiveAreaDeRisco: "P",
      archiveCercaEletronica: "P",
    };
  });

  const [movementForm, setMovementForm] = useState(() => {
    const stored = initialStoredState.movementForm;
    if (stored) {
      // Mantém dados do localStorage mas garante attachmentFormat válido
      return { ...stored, attachmentFormat: normalizeFormat(stored.attachmentFormat, "P") };
    }
    return {
      weekdays: { flag: false, start: "08:00", end: "18:00" },
      saturday: { flag: false, start: "08:00", end: "18:00" },
      sunday: { flag: false, start: "08:00", end: "18:00" },
      emailNotificationFlag: false,
      attachmentFormat: "P",
    };
  });

  const [speedForm, setSpeedForm] = useState(() => {
    const stored = initialStoredState.speedForm;
    if (stored) {
      return { ...stored, attachmentFormat: normalizeFormat(stored.attachmentFormat, "P") };
    }
    return {
      urban: { flag: false, value: "30" },
      highway: { flag: false, value: "30" },
      emailNotificationFlag: false,
      attachmentFormat: "P",
    };
  });

  const [reportConfigPayload, setReportConfigPayload] = useState(
    initialStoredState.reportConfigPayload || []
  );

  const [initialReportConfig, setInitialReportConfig] = useState(
    initialStoredState.reportConfigPayload || []
  );

  // Buscar as cores
  useEffect(() => {
    const fetchColors = async () => {
      try {
        const response = await colorService.list();
        if (response) {
          setListCollor(response);
        }
      } catch (error) {
        console.error("Erro ao buscar as cores:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchColors();
  }, []);

  // Buscar a configuração do grupo (depende apenas de vehicleGroupId)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const reportsConfig = await configsService.fetchGroupReportsConfiguration(
          vehicleGroupId
        );
        console.log("Reports Configuration:", reportsConfig);

        if (reportsConfig && Array.isArray(reportsConfig) && reportsConfig.length > 0) {
          const groupData = reportsConfig[0];
          setGroup(groupData);

          // Atribui os valores do grupo aos filtros
          if (groupData.colorId) {
            setSelectedId(groupData.colorId);
          }

          // Atribui flags booleanas
          setKmzFlag(groupData.emailKmzCheckFlag === "S");
          setFenceFlag(groupData.emailFenceCheckFlag === "S");
          setVisitFlag(groupData.emailVisitCheckFlag === "S");
          setRiskAreaFlag(groupData.emailRiskAreaCheckFlag === "S");
          setStrechesFlag(groupData.emailSegmentCheckFlag === "S");

          // Atribui configurações de relatórios
          setRelatorios((prev) => ({
            ...prev,
            kmz: groupData.emailKmzCheckFlag === "S",
            movimentacao: groupData.emailMovementCheckFlag === "S",
            pontoDeVista: groupData.emailVisitCheckFlag === "S",
            areaDeRisco: groupData.emailRiskAreaCheckFlag === "S",
            cercaEletronica: groupData.emailFenceCheckFlag === "S",
          }));

          // Atribui configurações de arquivos (normaliza valores do servidor)
          setArchives((prev) => ({
            ...prev,
            archiveKmz: normalizeFormat(groupData.emailKmzAttachmentFormat, "K"),
            archiveMovimentacao: normalizeFormat(groupData.emailMovementCheckAttachmentFormat, "P"),
            archivePontoDeVista: "P",
            archiveAreaDeRisco: "P",
            archiveCercaEletronica: "P",
          }));

          // Movimento: usa localStorage se existir (já sanitizado no useState),
          // senão usa dados da API (também normalizados)
          const storedMovement = initialStoredState.movementForm;
          const movementFormData = storedMovement
            ? { ...storedMovement, attachmentFormat: normalizeFormat(storedMovement.attachmentFormat, "P") }
            : {
                weekdays: {
                  flag: groupData.movementCheckMonToFriFlag === "S",
                  start: groupData.movementCheckMonToFriStart?.substring(0, 5) || "08:00",
                  end: groupData.movementCheckMonToFriEnd?.substring(0, 5) || "18:00",
                },
                saturday: {
                  flag: groupData.movementCheckSatFlag === "S",
                  start: groupData.movementCheckSatStart?.substring(0, 5) || "08:00",
                  end: groupData.movementCheckSatEnd?.substring(0, 5) || "18:00",
                },
                sunday: {
                  flag: groupData.movementCheckSunFlag === "S",
                  start: groupData.movementCheckSunStart?.substring(0, 5) || "08:00",
                  end: groupData.movementCheckSunEnd?.substring(0, 5) || "18:00",
                },
                emailNotificationFlag: groupData.emailMovementCheckFlag === "S",
                attachmentFormat: normalizeFormat(groupData.emailMovementCheckAttachmentFormat, "P"),
              };
          setMovementForm(movementFormData);

          // Velocidade: mesma estratégia (localStorage sanitizado > API normalizada)
          const storedSpeed = initialStoredState.speedForm;
          const speedFormData = storedSpeed
            ? { ...storedSpeed, attachmentFormat: normalizeFormat(storedSpeed.attachmentFormat, "P") }
            : {
                urban: {
                  flag: groupData.urbanSpeedCheckFlag === "S",
                  value: groupData.urbanSpeedCheckValue?.toString() || "30",
                },
                highway: {
                  flag: groupData.roadSpeedCheckFlag === "S",
                  value: groupData.roadSpeedCheckValue?.toString() || "30",
                },
                emailNotificationFlag: groupData.emailSpeedCheckFlag === "S",
                attachmentFormat: normalizeFormat(groupData.emailSpeedCheckAttachmentFormat, "P"),
              };
          setSpeedForm(speedFormData);

          const storedReportConfig = initialStoredState.reportConfigPayload;
          if (Array.isArray(storedReportConfig) && storedReportConfig.length > 0) {
            const normalizedStoredReportConfig = ensureHighSpeedDefaults(storedReportConfig);
            setInitialReportConfig(normalizedStoredReportConfig);
            setReportConfigPayload(normalizedStoredReportConfig);
            setRelatorios((prev) => ({
              ...prev,
              ...normalizedStoredReportConfig.reduce((acc, cfg) => {
                acc[cfg.id] = !!cfg.ativo;
                return acc;
              }, {}),
            }));
          } else {
            const mappedReportConfig = ensureHighSpeedDefaults(
              buildReportConfigFromApi(groupData)
            );
            setInitialReportConfig(mappedReportConfig);
            setReportConfigPayload(mappedReportConfig);
            setRelatorios((prev) => ({
              ...prev,
              ...mappedReportConfig.reduce((acc, cfg) => {
                acc[cfg.id] = !!cfg.ativo;
                return acc;
              }, {}),
            }));
          }
        }
      } catch (error) {
        console.error("Erro ao buscar as grupos:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleGroupId]);

  // Resolve a cor selecionada quando o grupo ou a lista de cores forem carregados
  useEffect(() => {
    if (group?.colorId && listCollor.length > 0) {
      const colorObj = listCollor.find((c) => c.colorId == group.colorId);
      if (colorObj) {
        setSelectedColor(colorObj.hex);
      }
    }
  }, [group?.colorId, listCollor]);

  return {
    isLoading,
    group,
    listCollor,
    selectedId,
    setSelectedId,
    selectedColor,
    setSelectedColor,
    kmzFlag,
    setKmzFlag,
    fenceFlag,
    setFenceFlag,
    visitFlag,
    setVisitFlag,
    riskAreaFlag,
    setRiskAreaFlag,
    strechesFlag,
    setStrechesFlag,
    relatorios,
    setRelatorios,
    archives,
    setArchives,
    movementForm,
    setMovementForm,
    speedForm,
    setSpeedForm,
    reportConfigPayload,
    setReportConfigPayload,
    initialReportConfig,
    setInitialReportConfig,
  };
}
